/**
 * Plans Tab for Admin Client Detail Page
 * Manages client plan subscriptions, plan entitlements, and entitlement add-ons.
 */
import { useCallback, useEffect, useState } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminEnums } from "@/hooks/use-admin-enums";
import { useAdminApi } from "@/services/api/admin-api";
import type {
    ClientEntitlementAdminListItemDto,
    ClientPlanAdminListItemDto,
    CreateClientEntitlementRequestDto,
    CreateClientPlanRequestDto,
    EntitlementAdminListItemDto,
    PlanAdminListItemDto,
    PlanEntitlementAdminListItemDto,
    UpdateClientEntitlementRequestDto,
    UpdateClientPlanRequestDto,
} from "@/types/api/admin";

// =============================================================================
// TYPES
// =============================================================================

interface PlansTabProps {
    clientId: number;
}

// =============================================================================
// ACTIVE PLAN SECTION
// =============================================================================

function ActivePlanSection({ clientId }: PlansTabProps) {
    const adminApi = useAdminApi();
    const { getOptions, isLoaded: enumsLoaded } = useAdminEnums();
    const [plans, setPlans] = useState<ClientPlanAdminListItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editEffectiveTo, setEditEffectiveTo] = useState("");
    const [editPageQuota, setEditPageQuota] = useState("");
    const [editDocumentQuota, setEditDocumentQuota] = useState("");
    const [editBillingCycle, setEditBillingCycle] = useState("");
    const [editContractId, setEditContractId] = useState("");
    const [editAutoRenew, setEditAutoRenew] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const fetchPlans = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listClientPlans(clientId, 100, 0);
            setPlans(res.data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load plans.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId]);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const activePlan = plans.find((p) => p.isActive);

    const billingCycleOptions: SelectItemType[] = enumsLoaded ? getOptions("BillingCycleEnum").map((opt) => ({ id: opt.label, label: opt.label })) : [];

    const startEditing = useCallback(() => {
        if (!activePlan) return;
        setEditEffectiveTo(activePlan.effectiveTo ? activePlan.effectiveTo.split("T")[0] : "");
        setEditPageQuota(activePlan.pageQuota != null ? String(activePlan.pageQuota) : "");
        setEditDocumentQuota(activePlan.documentQuota != null ? String(activePlan.documentQuota) : "");
        setEditBillingCycle(activePlan.billingCycle ?? "");
        setEditContractId(activePlan.contractId ?? "");
        setEditAutoRenew(activePlan.autoRenew);
        setIsEditing(true);
        setSaveError(null);
    }, [activePlan]);

    const handleSave = useCallback(async () => {
        if (!activePlan) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            const dto: UpdateClientPlanRequestDto = {
                effectiveTo: editEffectiveTo || null,
                pageQuota: editPageQuota ? Number(editPageQuota) : null,
                documentQuota: editDocumentQuota ? Number(editDocumentQuota) : null,
                billingCycle: editBillingCycle || null,
                contractId: editContractId || null,
                autoRenew: editAutoRenew,
            };
            await adminApi.updateClientPlan(clientId, activePlan.id, dto);
            setIsEditing(false);
            fetchPlans();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to update plan.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, activePlan, editEffectiveTo, editPageQuota, editDocumentQuota, editBillingCycle, editContractId, editAutoRenew, fetchPlans]);

    if (isLoading) {
        return (
            <section>
                <h3 className="text-lg font-semibold text-primary">Active Plan</h3>
                <div className="mt-4">
                    <LoadingIndicator size="sm" />
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section>
                <h3 className="text-lg font-semibold text-primary">Active Plan</h3>
                <p className="mt-2 text-sm text-error-primary">{error}</p>
            </section>
        );
    }

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Active Plan</h3>
                    <p className="mt-1 text-sm text-tertiary">Current plan subscription for this client.</p>
                </div>
                {activePlan && !isEditing && (
                    <Button color="secondary" size="sm" onClick={startEditing}>
                        Edit
                    </Button>
                )}
            </div>

            {!activePlan ? (
                <p className="mt-4 text-sm text-quaternary">No active plan assigned.</p>
            ) : isEditing ? (
                <div className="mt-4 grid max-w-xl grid-cols-1 gap-4">
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-secondary">Plan</label>
                        <div className="rounded-lg bg-disabled_subtle px-3 py-2 text-sm text-disabled">
                            {activePlan.planName} ({activePlan.planCode})
                        </div>
                    </div>
                    <Input label="Effective To" value={editEffectiveTo} onChange={setEditEffectiveTo} size="sm" type="date" />
                    <Input
                        label="Page Quota"
                        value={editPageQuota}
                        onChange={setEditPageQuota}
                        size="sm"
                        type="number"
                        placeholder="Leave empty for plan default"
                    />
                    <Input
                        label="Document Quota"
                        value={editDocumentQuota}
                        onChange={setEditDocumentQuota}
                        size="sm"
                        type="number"
                        placeholder="Leave empty for plan default"
                    />
                    {billingCycleOptions.length > 0 && (
                        <Select
                            label="Billing Cycle"
                            items={billingCycleOptions}
                            selectedKey={editBillingCycle || null}
                            onSelectionChange={(key) => setEditBillingCycle(String(key ?? ""))}
                            placeholder="Select..."
                            size="sm"
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                    )}
                    <Input label="Contract ID" value={editContractId} onChange={setEditContractId} size="sm" placeholder="External contract reference" />
                    <Toggle label="Auto Renew" isSelected={editAutoRenew} onChange={setEditAutoRenew} size="sm" />

                    {saveError && <p className="text-sm text-error-primary">{saveError}</p>}

                    <div className="flex gap-2">
                        <Button color="primary" size="sm" onClick={handleSave} isDisabled={isSaving}>
                            {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                        <Button color="secondary" size="sm" onClick={() => setIsEditing(false)} isDisabled={isSaving}>
                            Cancel
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="mt-4 grid max-w-xl grid-cols-2 gap-x-8 gap-y-3">
                    <DetailField label="Plan" value={`${activePlan.planName} (${activePlan.planCode})`} />
                    <DetailField label="Effective From" value={formatDate(activePlan.effectiveFrom)} />
                    <DetailField label="Effective To" value={activePlan.effectiveTo ? formatDate(activePlan.effectiveTo) : "No expiry"} />
                    <DetailField label="Page Quota" value={activePlan.pageQuota != null ? String(activePlan.pageQuota) : "Plan default"} />
                    <DetailField label="Document Quota" value={activePlan.documentQuota != null ? String(activePlan.documentQuota) : "Plan default"} />
                    <DetailField label="Billing Cycle" value={activePlan.billingCycle ?? "—"} />
                    <DetailField label="Contract ID" value={activePlan.contractId ?? "—"} />
                    <DetailField label="Auto Renew" value={activePlan.autoRenew ? "Yes" : "No"} />
                </div>
            )}
        </section>
    );
}

// =============================================================================
// PLAN HISTORY SECTION
// =============================================================================

function PlanHistorySection({ clientId }: PlansTabProps) {
    const adminApi = useAdminApi();
    const { getOptions, isLoaded: enumsLoaded } = useAdminEnums();
    const [plans, setPlans] = useState<ClientPlanAdminListItemDto[]>([]);
    const [allPlans, setAllPlans] = useState<PlanAdminListItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Add plan form
    const [showForm, setShowForm] = useState(false);
    const [newPlanId, setNewPlanId] = useState("");
    const [newEffectiveFrom, setNewEffectiveFrom] = useState("");
    const [newEffectiveTo, setNewEffectiveTo] = useState("");
    const [newPageQuota, setNewPageQuota] = useState("");
    const [newDocumentQuota, setNewDocumentQuota] = useState("");
    const [newBillingCycle, setNewBillingCycle] = useState("");
    const [newContractId, setNewContractId] = useState("");
    const [newAutoRenew, setNewAutoRenew] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [plansRes, catalogPlans] = await Promise.all([adminApi.listClientPlans(clientId, 100, 0), adminApi.listPlans()]);
            setPlans(plansRes.data);
            setAllPlans(catalogPlans);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load plan history.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const planOptions: SelectItemType[] = allPlans.filter((p) => p.isActive).map((p) => ({ id: String(p.id), label: `${p.name} (${p.code})` }));
    const billingCycleOptions: SelectItemType[] = enumsLoaded ? getOptions("BillingCycleEnum").map((opt) => ({ id: opt.label, label: opt.label })) : [];

    const resetForm = useCallback(() => {
        setNewPlanId("");
        setNewEffectiveFrom("");
        setNewEffectiveTo("");
        setNewPageQuota("");
        setNewDocumentQuota("");
        setNewBillingCycle("");
        setNewContractId("");
        setNewAutoRenew(true);
        setSaveError(null);
    }, []);

    const handleCreate = useCallback(async () => {
        if (!newPlanId || !newEffectiveFrom) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            const dto: CreateClientPlanRequestDto = {
                planId: Number(newPlanId),
                effectiveFrom: newEffectiveFrom,
                effectiveTo: newEffectiveTo || null,
                pageQuota: newPageQuota ? Number(newPageQuota) : null,
                documentQuota: newDocumentQuota ? Number(newDocumentQuota) : null,
                billingCycle: newBillingCycle || null,
                contractId: newContractId || null,
                autoRenew: newAutoRenew,
            };
            await adminApi.createClientPlan(clientId, dto);
            setShowForm(false);
            resetForm();
            fetchData();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to create plan.");
        } finally {
            setIsSaving(false);
        }
    }, [
        adminApi,
        clientId,
        newPlanId,
        newEffectiveFrom,
        newEffectiveTo,
        newPageQuota,
        newDocumentQuota,
        newBillingCycle,
        newContractId,
        newAutoRenew,
        fetchData,
        resetForm,
    ]);

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Plan History</h3>
                    <p className="mt-1 text-sm text-tertiary">All plan subscriptions for this client.</p>
                </div>
                {!showForm && (
                    <Button color="primary" size="sm" onClick={() => setShowForm(true)}>
                        Assign Plan
                    </Button>
                )}
            </div>

            {showForm && (
                <div className="mt-4 rounded-lg border border-secondary p-4">
                    <h4 className="mb-3 text-sm font-semibold text-primary">Assign New Plan</h4>
                    <div className="grid max-w-xl grid-cols-1 gap-4">
                        {planOptions.length > 0 && (
                            <Select
                                label="Plan"
                                items={planOptions}
                                selectedKey={newPlanId || null}
                                onSelectionChange={(key) => setNewPlanId(String(key ?? ""))}
                                placeholder="Select plan..."
                                size="sm"
                                isRequired
                            >
                                {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                            </Select>
                        )}
                        <Input label="Effective From" value={newEffectiveFrom} onChange={setNewEffectiveFrom} size="sm" type="date" isRequired />
                        <Input label="Effective To" value={newEffectiveTo} onChange={setNewEffectiveTo} size="sm" type="date" />
                        <Input
                            label="Page Quota"
                            value={newPageQuota}
                            onChange={setNewPageQuota}
                            size="sm"
                            type="number"
                            placeholder="Leave empty for plan default"
                        />
                        <Input
                            label="Document Quota"
                            value={newDocumentQuota}
                            onChange={setNewDocumentQuota}
                            size="sm"
                            type="number"
                            placeholder="Leave empty for plan default"
                        />
                        {billingCycleOptions.length > 0 && (
                            <Select
                                label="Billing Cycle"
                                items={billingCycleOptions}
                                selectedKey={newBillingCycle || null}
                                onSelectionChange={(key) => setNewBillingCycle(String(key ?? ""))}
                                placeholder="Select..."
                                size="sm"
                            >
                                {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                            </Select>
                        )}
                        <Input label="Contract ID" value={newContractId} onChange={setNewContractId} size="sm" placeholder="External contract reference" />
                        <Toggle label="Auto Renew" isSelected={newAutoRenew} onChange={setNewAutoRenew} size="sm" />

                        {saveError && <p className="text-sm text-error-primary">{saveError}</p>}

                        <div className="flex gap-2">
                            <Button color="primary" size="sm" onClick={handleCreate} isDisabled={isSaving || !newPlanId || !newEffectiveFrom}>
                                {isSaving ? "Creating..." : "Create"}
                            </Button>
                            <Button
                                color="secondary"
                                size="sm"
                                onClick={() => {
                                    setShowForm(false);
                                    resetForm();
                                }}
                                isDisabled={isSaving}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="mt-4">
                    <LoadingIndicator size="sm" />
                </div>
            ) : error ? (
                <p className="mt-2 text-sm text-error-primary">{error}</p>
            ) : plans.length === 0 ? (
                <p className="mt-4 text-sm text-quaternary">No plan history.</p>
            ) : (
                <div className="mt-4 overflow-x-auto rounded-lg border border-secondary">
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-secondary bg-secondary_subtle">
                            <tr>
                                <th className="px-4 py-3 font-medium text-secondary">Plan</th>
                                <th className="px-4 py-3 font-medium text-secondary">From</th>
                                <th className="px-4 py-3 font-medium text-secondary">To</th>
                                <th className="px-4 py-3 font-medium text-secondary">Quotas</th>
                                <th className="px-4 py-3 font-medium text-secondary">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary">
                            {plans.map((plan) => (
                                <tr key={plan.id} className="hover:bg-primary_hover">
                                    <td className="px-4 py-3 font-medium text-primary">
                                        {plan.planName} ({plan.planCode})
                                    </td>
                                    <td className="px-4 py-3 text-tertiary">{formatDate(plan.effectiveFrom)}</td>
                                    <td className="px-4 py-3 text-tertiary">{plan.effectiveTo ? formatDate(plan.effectiveTo) : "—"}</td>
                                    <td className="px-4 py-3 text-tertiary">
                                        {plan.pageQuota != null ? `${plan.pageQuota} pages` : "—"}
                                        {plan.pageQuota != null && plan.documentQuota != null ? " / " : ""}
                                        {plan.documentQuota != null ? `${plan.documentQuota} docs` : ""}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge color={plan.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                            {plan.isActive ? "Active" : "Inactive"}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

// =============================================================================
// PLAN ENTITLEMENTS SECTION
// =============================================================================

function PlanEntitlementsSection({ clientId }: PlansTabProps) {
    const adminApi = useAdminApi();
    const [entitlements, setEntitlements] = useState<PlanEntitlementAdminListItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        async function fetch() {
            setIsLoading(true);
            try {
                const data = await adminApi.listClientPlanEntitlements(clientId);
                if (!cancelled) setEntitlements(data);
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load plan entitlements.");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }
        fetch();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId]);

    return (
        <section>
            <h3 className="text-lg font-semibold text-primary">Plan Entitlements</h3>
            <p className="mt-1 text-sm text-tertiary">Entitlements included in the active plan (read-only).</p>

            {isLoading ? (
                <div className="mt-4">
                    <LoadingIndicator size="sm" />
                </div>
            ) : error ? (
                <p className="mt-2 text-sm text-error-primary">{error}</p>
            ) : entitlements.length === 0 ? (
                <p className="mt-4 text-sm text-quaternary">No plan entitlements (no active plan or plan has no entitlements).</p>
            ) : (
                <div className="mt-4 overflow-x-auto rounded-lg border border-secondary">
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-secondary bg-secondary_subtle">
                            <tr>
                                <th className="px-4 py-3 font-medium text-secondary">Code</th>
                                <th className="px-4 py-3 font-medium text-secondary">Name</th>
                                <th className="px-4 py-3 font-medium text-secondary">Category</th>
                                <th className="px-4 py-3 font-medium text-secondary">Enabled</th>
                                <th className="px-4 py-3 font-medium text-secondary">Limit</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary">
                            {entitlements.map((ent) => (
                                <tr key={ent.id} className="hover:bg-primary_hover">
                                    <td className="px-4 py-3 font-mono text-sm text-primary">{ent.entitlementCode}</td>
                                    <td className="px-4 py-3 text-tertiary">{ent.entitlementName}</td>
                                    <td className="px-4 py-3 text-tertiary">{ent.entitlementCategory ?? "—"}</td>
                                    <td className="px-4 py-3">
                                        <Badge color={ent.isEnabled ? "success" : "gray"} size="sm" type="pill-color">
                                            {ent.isEnabled ? "Yes" : "No"}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-tertiary">{ent.limitValue ?? "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

// =============================================================================
// CLIENT ENTITLEMENT ADD-ONS SECTION
// =============================================================================

function ClientEntitlementAddOnsSection({ clientId }: PlansTabProps) {
    const adminApi = useAdminApi();
    const [addOns, setAddOns] = useState<ClientEntitlementAdminListItemDto[]>([]);
    const [allEntitlements, setAllEntitlements] = useState<EntitlementAdminListItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Add form
    const [showForm, setShowForm] = useState(false);
    const [newEntitlementId, setNewEntitlementId] = useState("");
    const [newIsEnabled, setNewIsEnabled] = useState(true);
    const [newLimitValue, setNewLimitValue] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [addOnsRes, catalog] = await Promise.all([adminApi.listClientEntitlements(clientId, 100, 0), adminApi.listEntitlements()]);
            setAddOns(addOnsRes.data);
            setAllEntitlements(catalog);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load entitlement add-ons.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Filter catalog to exclude existing add-ons
    const existingIds = new Set(addOns.map((a) => a.entitlementId));
    const availableOptions: SelectItemType[] = allEntitlements
        .filter((e) => e.isActive && !existingIds.has(e.id))
        .map((e) => ({ id: String(e.id), label: `${e.name} (${e.code})` }));

    const handleCreate = useCallback(async () => {
        if (!newEntitlementId) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            const dto: CreateClientEntitlementRequestDto = {
                entitlementId: Number(newEntitlementId),
                isEnabled: newIsEnabled,
                limitValue: newLimitValue ? Number(newLimitValue) : null,
            };
            await adminApi.createClientEntitlement(clientId, dto);
            setShowForm(false);
            setNewEntitlementId("");
            setNewIsEnabled(true);
            setNewLimitValue("");
            fetchData();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to create add-on.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, newEntitlementId, newIsEnabled, newLimitValue, fetchData]);

    const handleToggle = useCallback(
        async (addOn: ClientEntitlementAdminListItemDto) => {
            try {
                const dto: UpdateClientEntitlementRequestDto = {
                    isEnabled: !addOn.isEnabled,
                    limitValue: addOn.limitValue,
                };
                await adminApi.updateClientEntitlement(clientId, addOn.id, dto);
                fetchData();
            } catch {
                // Silently fail — will be visible via stale state
            }
        },
        [adminApi, clientId, fetchData],
    );

    const handleDelete = useCallback(
        async (id: number) => {
            try {
                await adminApi.deleteClientEntitlement(clientId, id);
                fetchData();
            } catch {
                // Silently fail
            }
        },
        [adminApi, clientId, fetchData],
    );

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Entitlement Add-Ons</h3>
                    <p className="mt-1 text-sm text-tertiary">Extra entitlements beyond the plan defaults.</p>
                </div>
                {!showForm && (
                    <Button color="primary" size="sm" onClick={() => setShowForm(true)}>
                        Add Add-On
                    </Button>
                )}
            </div>

            {showForm && (
                <div className="mt-4 rounded-lg border border-secondary p-4">
                    <h4 className="mb-3 text-sm font-semibold text-primary">Add Entitlement Add-On</h4>
                    <div className="grid max-w-xl grid-cols-1 gap-4">
                        {availableOptions.length > 0 ? (
                            <Select
                                label="Entitlement"
                                items={availableOptions}
                                selectedKey={newEntitlementId || null}
                                onSelectionChange={(key) => setNewEntitlementId(String(key ?? ""))}
                                placeholder="Select entitlement..."
                                size="sm"
                                isRequired
                            >
                                {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                            </Select>
                        ) : (
                            <p className="text-sm text-quaternary">All entitlements are already assigned.</p>
                        )}
                        <Toggle label="Enabled" isSelected={newIsEnabled} onChange={setNewIsEnabled} size="sm" />
                        <Input
                            label="Limit Value"
                            value={newLimitValue}
                            onChange={setNewLimitValue}
                            size="sm"
                            type="number"
                            placeholder="Optional"
                            isDisabled={!newIsEnabled}
                        />

                        {saveError && <p className="text-sm text-error-primary">{saveError}</p>}

                        <div className="flex gap-2">
                            <Button color="primary" size="sm" onClick={handleCreate} isDisabled={isSaving || !newEntitlementId}>
                                {isSaving ? "Adding..." : "Add"}
                            </Button>
                            <Button
                                color="secondary"
                                size="sm"
                                onClick={() => {
                                    setShowForm(false);
                                    setSaveError(null);
                                }}
                                isDisabled={isSaving}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="mt-4">
                    <LoadingIndicator size="sm" />
                </div>
            ) : error ? (
                <p className="mt-2 text-sm text-error-primary">{error}</p>
            ) : addOns.length === 0 ? (
                <p className="mt-4 text-sm text-quaternary">No entitlement add-ons configured.</p>
            ) : (
                <div className="mt-4 overflow-x-auto rounded-lg border border-secondary">
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-secondary bg-secondary_subtle">
                            <tr>
                                <th className="px-4 py-3 font-medium text-secondary">Code</th>
                                <th className="px-4 py-3 font-medium text-secondary">Name</th>
                                <th className="px-4 py-3 font-medium text-secondary">Category</th>
                                <th className="px-4 py-3 font-medium text-secondary">Enabled</th>
                                <th className="px-4 py-3 font-medium text-secondary">Limit</th>
                                <th className="px-4 py-3 font-medium text-secondary">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary">
                            {addOns.map((addOn) => (
                                <tr key={addOn.id} className="hover:bg-primary_hover">
                                    <td className="px-4 py-3 font-mono text-sm text-primary">{addOn.entitlementCode}</td>
                                    <td className="px-4 py-3 text-tertiary">{addOn.entitlementName}</td>
                                    <td className="px-4 py-3 text-tertiary">{addOn.entitlementCategory ?? "—"}</td>
                                    <td className="px-4 py-3">
                                        <Toggle isSelected={addOn.isEnabled} onChange={() => handleToggle(addOn)} size="sm" />
                                    </td>
                                    <td className="px-4 py-3 text-tertiary">{addOn.limitValue ?? "—"}</td>
                                    <td className="px-4 py-3">
                                        <Button color="tertiary-destructive" size="sm" onClick={() => handleDelete(addOn.id)}>
                                            Remove
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

// =============================================================================
// HELPERS
// =============================================================================

function DetailField({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs font-medium text-tertiary">{label}</p>
            <p className="mt-0.5 text-sm text-primary">{value}</p>
        </div>
    );
}

function formatDate(dateStr: string): string {
    try {
        return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return dateStr;
    }
}

// =============================================================================
// PLANS TAB
// =============================================================================

export function PlansTab({ clientId }: PlansTabProps) {
    return (
        <div className="space-y-10">
            <ActivePlanSection clientId={clientId} />
            <PlanHistorySection clientId={clientId} />
            <PlanEntitlementsSection clientId={clientId} />
            <ClientEntitlementAddOnsSection clientId={clientId} />
        </div>
    );
}
